export const lessonsData = [
  {
    id: 1,
    title: "Addition & Subtraction within 100",
    vocabulary: [
      { term: "Addend", definition: "A number being added" },
      { term: "Sum", definition: "The answer to an addition problem" },
      { term: "Minuend", definition: "The number you start with in subtraction" },
      { term: "Subtrahend", definition: "The number being subtracted" },
      { term: "Difference", definition: "The answer to a subtraction problem" },
      { term: "Regrouping", definition: "Trading 10 ones for 1 ten, or 1 ten for 10 ones" }
    ],
    conceptExplanation: `When we add or subtract two-digit numbers, we line up the ones and tens carefully.

**Addition with Regrouping:**
When the ones column adds up to 10 or more, we regroup by carrying 1 ten to the tens column.

**Subtraction with Regrouping:**
When we can't subtract the ones digit (because it's smaller), we borrow 1 ten from the tens place and turn it into 10 ones.

**The Standard Algorithm:**
This is the method where we stack numbers vertically and work column by column, starting from the ones place.`,
    examples: [
      {
        problem: "47 + 36",
        steps: [
          "Step 1: Line up the numbers by place value. Write 47 on top and 36 below it, with ones under ones and tens under tens.",
          "Step 2: Add the ones: 7 + 6 = 13. That's more than 10!",
          "Step 3: Write the 3 in the ones place and carry the 1 ten to the tens column.",
          "Step 4: Add the tens: 4 + 3 + 1 (carried) = 8 tens.",
          "Step 5: Final answer: 83"
        ]
      },
      {
        problem: "82 - 37",
        steps: [
          "Step 1: Line up the numbers. Write 82 on top and 37 below it.",
          "Step 2: Look at the ones: Can we subtract 7 from 2? No! We need to regroup.",
          "Step 3: Borrow 1 ten from the 8 tens. Now we have 7 tens and 12 ones.",
          "Step 4: Subtract the ones: 12 - 7 = 5",
          "Step 5: Subtract the tens: 7 - 3 = 4",
          "Step 6: Final answer: 45"
        ]
      },
      {
        problem: "55 + 28",
        steps: [
          "Step 1: Stack the numbers with ones and tens aligned.",
          "Step 2: Add the ones: 5 + 8 = 13",
          "Step 3: Write 3 in the ones place, carry 1 to the tens.",
          "Step 4: Add the tens: 5 + 2 + 1 = 8",
          "Step 5: Final answer: 83"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What is 46 + 27?",
        options: ["63", "73", "83", "69"],
        correctAnswer: 1,
        explanation: "Add ones: 6 + 7 = 13 (write 3, carry 1). Add tens: 4 + 2 + 1 = 7. Answer: 73"
      },
      {
        question: "What is 91 - 48?",
        options: ["53", "43", "47", "57"],
        correctAnswer: 1,
        explanation: "Regroup: 8 tens and 11 ones. Subtract ones: 11 - 8 = 3. Subtract tens: 8 - 4 = 4. Answer: 43"
      },
      {
        question: "What is 34 + 59?",
        options: ["83", "93", "92", "82"],
        correctAnswer: 1,
        explanation: "Add ones: 4 + 9 = 13 (write 3, carry 1). Add tens: 3 + 5 + 1 = 9. Answer: 93"
      },
      {
        question: "What is 70 - 26?",
        options: ["54", "44", "46", "56"],
        correctAnswer: 1,
        explanation: "Regroup: 6 tens and 10 ones. Subtract ones: 10 - 6 = 4. Subtract tens: 6 - 2 = 4. Answer: 44"
      }
    ],
    teacherTip: "Always start with the ones place! And remember: when you regroup in subtraction, you're not making the number smaller—you're just trading 1 ten for 10 ones so you have enough to subtract."
  },
  {
    id: 2,
    title: "Place Value to 1,000",
    vocabulary: [
      { term: "Digit", definition: "A single number symbol (0-9)" },
      { term: "Place Value", definition: "The value of a digit based on its position" },
      { term: "Hundreds", definition: "The third place from the right (worth 100 each)" },
      { term: "Tens", definition: "The second place from the right (worth 10 each)" },
      { term: "Ones", definition: "The first place from the right (worth 1 each)" },
      { term: "Expanded Form", definition: "Writing a number as the sum of the value of its digits" }
    ],
    conceptExplanation: `Every digit in a number has a value based on where it sits.

**Place Value Chart:**
Hundreds | Tens | Ones
    3    |  4   |  7

The number 347 means:
• 3 hundreds = 300
• 4 tens = 40
• 7 ones = 7

**Expanded Form:** 347 = 300 + 40 + 7

**Comparing Numbers:**
Start from the left (hundreds place). The number with more hundreds is greater. If hundreds are equal, compare tens. If tens are equal, compare ones.`,
    examples: [
      {
        problem: "Write 582 in expanded form",
        steps: [
          "Step 1: Identify the place value of each digit.",
          "Step 2: The 5 is in the hundreds place: 5 × 100 = 500",
          "Step 3: The 8 is in the tens place: 8 × 10 = 80",
          "Step 4: The 2 is in the ones place: 2 × 1 = 2",
          "Step 5: Expanded form: 500 + 80 + 2"
        ]
      },
      {
        problem: "Compare 456 and 463. Which is greater?",
        steps: [
          "Step 1: Line up the numbers and compare place by place.",
          "Step 2: Compare hundreds: Both have 4 hundreds. They're equal so far.",
          "Step 3: Compare tens: Both have 6 tens. Still equal.",
          "Step 4: Compare ones: 6 ones vs. 3 ones. 6 > 3",
          "Step 5: Therefore, 463 > 456"
        ]
      },
      {
        problem: "What number is 700 + 30 + 9?",
        steps: [
          "Step 1: Identify each part: 700 is hundreds, 30 is tens, 9 is ones.",
          "Step 2: 700 means 7 in the hundreds place.",
          "Step 3: 30 means 3 in the tens place.",
          "Step 4: 9 means 9 in the ones place.",
          "Step 5: The number is 739"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What is the value of the digit 6 in the number 624?",
        options: ["6", "60", "600", "6,000"],
        correctAnswer: 2,
        explanation: "The 6 is in the hundreds place, so its value is 6 × 100 = 600"
      },
      {
        question: "Which number is greater: 789 or 798?",
        options: ["789", "798", "They are equal", "Cannot tell"],
        correctAnswer: 1,
        explanation: "Both have 7 hundreds. Compare tens: 9 tens > 8 tens. So 798 > 789"
      },
      {
        question: "What is 400 + 50 + 3 in standard form?",
        options: ["4,053", "453", "4,503", "435"],
        correctAnswer: 1,
        explanation: "4 hundreds + 5 tens + 3 ones = 453"
      },
      {
        question: "How many tens are in 860?",
        options: ["6", "8", "86", "60"],
        correctAnswer: 2,
        explanation: "860 has 86 tens total (8 groups of 10 in the tens place, plus 80 more from the hundreds)"
      }
    ],
    teacherTip: "Think of place value like money! 347 is like having 3 hundred-dollar bills, 4 ten-dollar bills, and 7 one-dollar bills. It helps you see why each digit's position matters."
  },
  {
    id: 3,
    title: "Addition & Subtraction within 1,000",
    vocabulary: [
      { term: "Three-Digit Number", definition: "A number with hundreds, tens, and ones" },
      { term: "Regroup", definition: "Trade between place values (10 ones = 1 ten, 10 tens = 1 hundred)" },
      { term: "Estimate", definition: "Find an approximate answer by rounding" },
      { term: "Mental Math", definition: "Solving problems in your head without writing" }
    ],
    conceptExplanation: `Adding and subtracting three-digit numbers uses the same regrouping skills as two-digit numbers—we just have one more place value to work with!

**Addition Strategy:**
1. Line up by place value
2. Add ones first (regroup if needed)
3. Add tens (don't forget any carried number!)
4. Add hundreds

**Subtraction Strategy:**
1. Line up by place value
2. Start with ones (borrow if needed)
3. Subtract tens (borrow from hundreds if needed)
4. Subtract hundreds

**Checking Your Work:**
For addition, try adding in a different order. For subtraction, add your answer to the smaller number—you should get the larger number back!`,
    examples: [
      {
        problem: "456 + 378",
        steps: [
          "Step 1: Stack the numbers, aligning place values.",
          "Step 2: Add ones: 6 + 8 = 14. Write 4, carry 1 to tens.",
          "Step 3: Add tens: 5 + 7 + 1 (carried) = 13. Write 3, carry 1 to hundreds.",
          "Step 4: Add hundreds: 4 + 3 + 1 (carried) = 8",
          "Step 5: Final answer: 834"
        ]
      },
      {
        problem: "625 - 347",
        steps: [
          "Step 1: Stack the numbers with 625 on top.",
          "Step 2: Ones: Can't do 5 - 7. Borrow from tens. Now 1 ten and 15 ones.",
          "Step 3: Subtract ones: 15 - 7 = 8",
          "Step 4: Tens: Can't do 1 - 4. Borrow from hundreds. Now 5 hundreds and 11 tens.",
          "Step 5: Subtract tens: 11 - 4 = 7",
          "Step 6: Subtract hundreds: 5 - 3 = 2",
          "Step 7: Final answer: 278"
        ]
      },
      {
        problem: "700 - 428",
        steps: [
          "Step 1: Write 700 on top and 428 below.",
          "Step 2: We need to regroup twice! 700 = 6 hundreds, 9 tens, 10 ones",
          "Step 3: Subtract ones: 10 - 8 = 2",
          "Step 4: Subtract tens: 9 - 2 = 7",
          "Step 5: Subtract hundreds: 6 - 4 = 2",
          "Step 6: Final answer: 272"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "What is 547 + 286?",
        options: ["823", "833", "733", "843"],
        correctAnswer: 1,
        explanation: "Ones: 7+6=13 (write 3, carry 1). Tens: 4+8+1=13 (write 3, carry 1). Hundreds: 5+2+1=8. Answer: 833"
      },
      {
        question: "What is 812 - 456?",
        options: ["356", "366", "346", "456"],
        correctAnswer: 0,
        explanation: "Regroup as needed. Ones: 12-6=6. Tens: 0 becomes 10 (borrow), 10-5=5. Hundreds: 7-4=3. Answer: 356"
      },
      {
        question: "What is 600 - 237?",
        options: ["363", "373", "463", "473"],
        correctAnswer: 0,
        explanation: "Regroup 600 as 5 hundreds, 9 tens, 10 ones. Then subtract: 10-7=3, 9-3=6, 5-2=3. Answer: 363"
      },
      {
        question: "What is 395 + 428?",
        options: ["713", "823", "813", "723"],
        correctAnswer: 1,
        explanation: "Ones: 5+8=13 (write 3, carry 1). Tens: 9+2+1=12 (write 2, carry 1). Hundreds: 3+4+1=8. Answer: 823"
      }
    ],
    teacherTip: "When subtracting from numbers with zeros (like 600 or 800), you might need to regroup multiple times. Take it slow and regroup one place at a time—you've got this!"
  },
  {
    id: 4,
    title: "Measurement: Length & Number Lines",
    vocabulary: [
      { term: "Inch", definition: "A unit of length in the customary system (about the width of a quarter)" },
      { term: "Centimeter", definition: "A unit of length in the metric system (about the width of a pinky finger)" },
      { term: "Ruler", definition: "A tool used to measure length" },
      { term: "Estimate", definition: "Make a reasonable guess about a measurement" },
      { term: "Number Line", definition: "A line with numbers marked at equal intervals" }
    ],
    conceptExplanation: `Measurement helps us describe how long, tall, or wide something is.

**Using a Ruler:**
• Line up the zero mark with one end of the object
• Read the number at the other end
• Count the marks carefully!

**Inches vs. Centimeters:**
• Inches are bigger (1 inch ≈ 2.5 centimeters)
• Centimeters are smaller and more precise

**Number Lines for Measurement:**
We can show measurements and solve addition/subtraction problems by jumping along a number line. Each jump represents a unit of measurement.`,
    examples: [
      {
        problem: "A pencil measures from the 0 mark to the 5 mark on an inch ruler. How long is it?",
        steps: [
          "Step 1: Find where the pencil starts: at 0",
          "Step 2: Find where the pencil ends: at 5",
          "Step 3: Count the spaces between: 0 to 5 is 5 spaces",
          "Step 4: The pencil is 5 inches long"
        ]
      },
      {
        problem: "Use a number line to solve 23 + 15",
        steps: [
          "Step 1: Start at 23 on the number line",
          "Step 2: We need to add 15, so we'll jump forward",
          "Step 3: Jump forward 10 to land on 33",
          "Step 4: Jump forward 5 more to land on 38",
          "Step 5: Answer: 23 + 15 = 38"
        ]
      },
      {
        problem: "A ribbon is 12 centimeters long. You cut off 5 centimeters. How long is it now?",
        steps: [
          "Step 1: Start with 12 centimeters",
          "Step 2: You're cutting off (subtracting) 5 centimeters",
          "Step 3: Use a number line: start at 12, jump back 5",
          "Step 4: 12 - 5 = 7",
          "Step 5: The ribbon is now 7 centimeters long"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "A crayon measures from 0 to 8 on a centimeter ruler. How long is the crayon?",
        options: ["7 cm", "8 cm", "9 cm", "10 cm"],
        correctAnswer: 1,
        explanation: "The crayon goes from 0 to 8, which is 8 centimeters long"
      },
      {
        question: "On a number line, you start at 45 and jump back 12. Where do you land?",
        options: ["33", "57", "43", "35"],
        correctAnswer: 0,
        explanation: "45 - 12 = 33. Jumping back means subtracting"
      },
      {
        question: "Which is longer: 10 inches or 10 centimeters?",
        options: ["10 inches", "10 centimeters", "They are equal", "Cannot tell"],
        correctAnswer: 0,
        explanation: "Inches are bigger units than centimeters, so 10 inches is longer than 10 centimeters"
      },
      {
        question: "A string is 25 cm long. You add another piece that is 18 cm. What is the total length?",
        options: ["43 cm", "33 cm", "53 cm", "42 cm"],
        correctAnswer: 0,
        explanation: "25 + 18 = 43 centimeters total"
      }
    ],
    teacherTip: "When measuring, always start at zero! And remember: the number line is your friend for visualizing addition and subtraction—it's like taking steps forward or backward."
  },
  {
    id: 5,
    title: "Time & Money",
    vocabulary: [
      { term: "Hour Hand", definition: "The short hand on a clock that shows hours" },
      { term: "Minute Hand", definition: "The long hand on a clock that shows minutes" },
      { term: "Quarter Hour", definition: "15 minutes (one-fourth of an hour)" },
      { term: "Half Hour", definition: "30 minutes (one-half of an hour)" },
      { term: "Penny", definition: "1 cent (¢)" },
      { term: "Nickel", definition: "5 cents" },
      { term: "Dime", definition: "10 cents" },
      { term: "Quarter", definition: "25 cents" },
      { term: "Dollar", definition: "100 cents ($1.00)" }
    ],
    conceptExplanation: `**Telling Time to 5 Minutes:**
The clock face is divided into 12 hours and 60 minutes. Each number represents 5 minutes when counting by the minute hand.
• 12 = 0 minutes (top)
• 1 = 5 minutes
• 2 = 10 minutes
• 3 = 15 minutes (quarter past)
• 6 = 30 minutes (half past)
• 9 = 45 minutes (quarter to the next hour)

**Counting Money:**
Start with the coins worth the most, then count up:
1. Quarters (25¢ each)
2. Dimes (10¢ each)
3. Nickels (5¢ each)
4. Pennies (1¢ each)

**Dollar Bills:**
$1 = 100 cents, $5 = 500 cents, $10 = 1,000 cents`,
    examples: [
      {
        problem: "What time is shown when the hour hand is between 3 and 4, and the minute hand points to 7?",
        steps: [
          "Step 1: The hour hand is between 3 and 4, so the hour is 3",
          "Step 2: The minute hand points to 7",
          "Step 3: Count by 5s: 7 × 5 = 35 minutes",
          "Step 4: The time is 3:35"
        ]
      },
      {
        problem: "Count these coins: 2 quarters, 1 dime, 3 nickels, 4 pennies",
        steps: [
          "Step 1: Start with quarters: 2 × 25¢ = 50¢",
          "Step 2: Add dimes: 50¢ + 10¢ = 60¢",
          "Step 3: Add nickels: 60¢ + 5¢ + 5¢ + 5¢ = 75¢",
          "Step 4: Add pennies: 75¢ + 1¢ + 1¢ + 1¢ + 1¢ = 79¢",
          "Step 5: Total: 79 cents"
        ]
      },
      {
        problem: "You have $1.00. You buy a toy for 65¢. How much change do you get?",
        steps: [
          "Step 1: You start with $1.00 = 100 cents",
          "Step 2: The toy costs 65 cents",
          "Step 3: Subtract: 100¢ - 65¢ = 35¢",
          "Step 4: Your change is 35 cents"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "The minute hand points to 9. How many minutes is that?",
        options: ["9 minutes", "45 minutes", "35 minutes", "50 minutes"],
        correctAnswer: 1,
        explanation: "When the minute hand points to 9, count by 5s nine times: 9 × 5 = 45 minutes"
      },
      {
        question: "How much money is 3 dimes and 2 nickels?",
        options: ["35¢", "40¢", "50¢", "45¢"],
        correctAnswer: 1,
        explanation: "3 dimes = 30¢, 2 nickels = 10¢. Total: 30¢ + 10¢ = 40¢"
      },
      {
        question: "What time is it when the hour hand is between 7 and 8, and the minute hand points to 3?",
        options: ["7:15", "8:15", "7:03", "3:07"],
        correctAnswer: 0,
        explanation: "Hour is 7 (hand between 7 and 8). Minute hand at 3 means 15 minutes. Time: 7:15"
      },
      {
        question: "How many quarters equal one dollar?",
        options: ["2", "3", "4", "5"],
        correctAnswer: 2,
        explanation: "4 quarters = 4 × 25¢ = 100¢ = $1.00"
      }
    ],
    teacherTip: "For time, remember: the hour hand moves slowly and shows which hour we're in. The minute hand moves fast and tells us how many minutes past the hour. For money, always start counting with the biggest coins first!"
  },
  {
    id: 6,
    title: "Geometry & Arrays",
    vocabulary: [
      { term: "Array", definition: "Objects arranged in equal rows and columns" },
      { term: "Row", definition: "A horizontal line of objects (goes left to right)" },
      { term: "Column", definition: "A vertical line of objects (goes up and down)" },
      { term: "Repeated Addition", definition: "Adding the same number multiple times" },
      { term: "Partition", definition: "Divide a shape into equal parts" },
      { term: "Equal Shares", definition: "Parts that are the same size" }
    ],
    conceptExplanation: `**Arrays:**
An array is a way to organize objects in equal rows and columns. Arrays help us see multiplication and repeated addition!

Example: 3 rows of 4 stars
★★★★
★★★★
★★★★

This is 3 groups of 4, or 4 + 4 + 4 = 12 stars

**Partitioning Shapes:**
We can divide shapes into equal parts:
• 2 equal parts = halves
• 3 equal parts = thirds
• 4 equal parts = fourths (or quarters)

Each part must be the same size to be equal shares!`,
    examples: [
      {
        problem: "Draw an array with 4 rows and 5 columns. How many objects total?",
        steps: [
          "Step 1: Draw 4 rows (horizontal lines)",
          "Step 2: Put 5 objects in each row",
          "Step 3: Count: 5 + 5 + 5 + 5 = 20",
          "Step 4: Or think: 4 rows × 5 in each row = 20 objects total"
        ]
      },
      {
        problem: "A rectangle is divided into 4 equal parts. What fraction is each part?",
        steps: [
          "Step 1: The whole rectangle is divided into 4 equal shares",
          "Step 2: Each share is 1 out of 4 parts",
          "Step 3: We write this as 1/4",
          "Step 4: Each part is one-fourth (or one quarter) of the rectangle"
        ]
      },
      {
        problem: "There are 3 rows of 6 apples. Write this as repeated addition and find the total.",
        steps: [
          "Step 1: 3 rows means we add 6 three times",
          "Step 2: Repeated addition: 6 + 6 + 6",
          "Step 3: Add: 6 + 6 = 12, then 12 + 6 = 18",
          "Step 4: Total: 18 apples"
        ]
      }
    ],
    practiceQuestions: [
      {
        question: "An array has 5 rows with 3 objects in each row. How many objects total?",
        options: ["8", "15", "12", "18"],
        correctAnswer: 1,
        explanation: "5 rows of 3 means 3 + 3 + 3 + 3 + 3 = 15 objects"
      },
      {
        question: "A circle is divided into 2 equal parts. What is each part called?",
        options: ["Thirds", "Fourths", "Halves", "Wholes"],
        correctAnswer: 2,
        explanation: "2 equal parts are called halves. Each part is 1/2 of the whole"
      },
      {
        question: "Which shows 4 + 4 + 4 as an array?",
        options: ["3 rows of 4", "4 rows of 3", "4 rows of 4", "3 rows of 3"],
        correctAnswer: 0,
        explanation: "4 + 4 + 4 means adding 4 three times, so it's 3 rows with 4 in each row"
      },
      {
        question: "A square is divided into 4 equal parts. What fraction is 1 part?",
        options: ["1/2", "1/3", "1/4", "1/5"],
        correctAnswer: 2,
        explanation: "1 part out of 4 equal parts is 1/4 (one-fourth)"
      }
    ],
    teacherTip: "Arrays are everywhere! Think of egg cartons, muffin tins, or classroom desks. When you see equal rows and columns, you're seeing an array—and that's the beginning of understanding multiplication!"
  }
];